import type { FlatStep, ProjectContent, ProjectStep, StepId } from "../projectTypes";

// Turns parts -> steps into one list, remembering where each step came from
export const flattenSteps = (content?: ProjectContent | null): FlatStep[] =>
    (content?.parts ?? []).flatMap((part, partIndex) =>
        (part.steps ?? []).map((step, stepIndex) => ({
            ...step,
            partId: part.id,
            partName: part.name,
            partIndex,
            stepIndex
        }))
    );

// The main editor's starter code, or the first editor's when there is no "main" slot
export type FileMap = Record<string, string>; // slot -> code

// The starter code of a step, by slot. Slots without starter code are left out, so a step that
// continues the previous one keeps what the learner already wrote.
export const getStarterFiles = (step: ProjectStep | undefined, slots: string[]): FileMap => {
    const files: FileMap = {};

    for (const slot of slots) {
        // A program has one editor: older projects may name its slot differently
        const editor =
            step?.editors?.find((item) => item.slot === slot) ??
            (slots.length === 1 ? step?.editors?.[0] : undefined);

        if (editor?.starterCode) files[slot] = editor.starterCode;
    }

    return files;
};

// The page the preview shows: the three files put together
export const buildPreviewDocument = (files: FileMap): string => {
    const { html = "", css = "", js = "" } = files;

    const style = css ? `<style>${css}</style>` : "";
    // A "</script>" inside the learner's code would end our script early
    const script = js ? `<script>${js.replace(/<\/script/gi, "<\\/script")}</script>` : "";

    // The learner may write a whole document: the style goes into its head, the script before its end.
    // (Function replacers: "$&" in the code must not be read as a replacement pattern.)
    if (/<html[\s>]/i.test(html)) {
        const withStyle = /<\/head>/i.test(html)
            ? html.replace(/<\/head>/i, () => `${style}</head>`)
            : html.replace(/<html[^>]*>/i, (tag) => `${tag}<head>${style}</head>`);

        return /<\/body>/i.test(withStyle)
            ? withStyle.replace(/<\/body>/i, () => `${script}</body>`)
            : withStyle + script;
    }

    return `<!doctype html><html><head><meta charset="utf-8">${style}</head><body>${html}${script}</body></html>`;
};

// A step can be opened if it is current or earlier, or directly follows a completed one
export const isStepReachable = (
    index: number,
    currentIndex: number,
    steps: FlatStep[],
    completedIds: Set<StepId>
): boolean => {
    const previous = steps[index - 1];
    return index <= currentIndex || (!!previous && completedIds.has(previous.id));
};